import { useCallback, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { useHistory, useLocation } from 'react-router-dom';
import HTTPRequest from '../api/HTTPRequest';
import probeMerchantMigration from '../api/probeMerchantMigration';
import buildNewUiRedirectUrl from '../util/buildNewUiRedirectUrl';
import readPayloadFromHash from '../util/readPayloadFromHash';
import { NEW_PAYMENT_API_ORIGIN, NEW_PAYMENT_UI_ORIGIN } from '../config';
import { SET_ERROR, SET_PAYLOAD, SET_RECURRING_PAYMENT_DETAILS, store } from '../store';
import CenteredLoader from './CenteredLoader';

function StartupLoader({ children }) {
    const [isLoading, setLoading] = useState(true);
    // Per-merchant cutover gate: 'checking' until we know, 'stay' to keep using
    // this (old) UI, 'redirecting' while navigating away to the new UI.
    const [migrationState, setMigrationState] = useState('checking');

    const history = useHistory();
    const { pathname } = useLocation();
    const { state, dispatch } = useContext(store);
    const { payload } = state;
    const { accessToken, merchant } = payload || {};
    const setPayload = value => dispatch({ type: SET_PAYLOAD, payload: value });

    const redirectPage = useCallback(() => {
        HTTPRequest({
            accessToken,
            path: `/payments/forward/accounts/merchant/${merchant}/authorised`,
        })
            .then(({ data }) => {
                if (data === true) {
                    // if authorised on our server, the payment registration is probably still pending.
                    history.replace('/redirect?resultCode=Authorised');
                } else {
                    history.replace('/new-payment-method');
                }

                setLoading(false);
            })
            .catch(error => {
                const frozenOrClosed = error?.data?.includes('frozen') || error?.data?.includes('closed');
                const incorrectOrIncomplete = error?.data?.includes('incorrect or incomplete');
                const unauthorised = error?.data?.includes('unauthorised active recurring payment methods found');
                const edenredRefreshFailed = error?.data?.includes('/connect/token');

                if (frozenOrClosed || unauthorised || incorrectOrIncomplete || edenredRefreshFailed) {
                    if (unauthorised && error.data.includes('EDENRED')) {
                        history.replace('/new-payment-method/edenred');
                    } else if (unauthorised && error.data.includes('ADYEN')) {
                        history.replace('/new-payment-method/adyen');
                    } else {
                        history.replace('/new-payment-method');
                    }
                    setLoading(false);
                } else {
                    dispatch({ type: SET_ERROR, payload: true });
                }
            });
    }, [accessToken, merchant]);

    const deleteAccount = useCallback(() => {
        HTTPRequest({
            accessToken,
            path: `/payments/forward/accounts/merchant/${merchant}/paymentmethod/action-delete`,
        })
            .then(() => {
                history.replace('/');

                // Reset the page
                dispatch({ type: SET_PAYLOAD, payload: {} });
                setTimeout(() => dispatch({ type: SET_PAYLOAD, payload }), 100);
            })
            .catch(() => {});
    }, [accessToken, merchant, redirectPage]);

    useEffect(() => {
        window.ReactNativeWebView?.postMessage(`Payload ${JSON.stringify(window.payload)}`);
        window.setPayload = setPayload; // used by fridge-mobile-app (android)

        // Direct injection always wins; the URL fragment is the fallback used by the
        // hand-off redirect from the new payment UI (payment-web-ui).
        const initialPayload = window.payload || readPayloadFromHash();
        initialPayload && setPayload(initialPayload);
    }, []);

    // Per-merchant cutover probe. Runs before the old-backend logic below: if the
    // new payment service returns any payment-method entries for this merchant,
    // redirect to the new UI. Anything else (empty data / 404 / 401 / 5xx /
    // network / unconfigured) falls through to this old UI (fail-safe). Skipped on
    // /redirect (Adyen result page).
    useEffect(() => {
        if (pathname === '/redirect') {
            setMigrationState('stay');
            return;
        }

        // Wait until the mobile app has injected the payload.
        if (!accessToken || !merchant) return;

        // Kill-switch: unset env vars => behave exactly as before.
        if (!NEW_PAYMENT_API_ORIGIN || !NEW_PAYMENT_UI_ORIGIN) {
            setMigrationState('stay');
            return;
        }

        let cancelled = false;

        probeMerchantMigration({
            newApiOrigin: NEW_PAYMENT_API_ORIGIN,
            merchant,
            accessToken,
        }).then(migrated => {
            if (cancelled) return;

            if (migrated) {
                setMigrationState('redirecting');
                window.location.replace(buildNewUiRedirectUrl(NEW_PAYMENT_UI_ORIGIN, payload));
            } else {
                setMigrationState('stay');
            }
        });

        return () => { cancelled = true; };
    }, [accessToken, merchant, pathname, payload]);

    useEffect(() => {
        if (migrationState !== 'stay') return;
        if (!accessToken || !merchant) return;

        HTTPRequest({
            accessToken,
            path: `/payments/forward/accounts/merchant/${merchant}/stored-payment-methods`,
        })
            .then(({ data }) => {
                if (data?.length === 0) {
                    redirectPage();
                } else if (
                    data?.length === 1 &&
                    data[0].paymentProcessor === 'ADYEN' &&
                    data[0].paymentDetails?.details?.length === 0  // initially after ideal registration for example)
                ) {
                    const creationDate = dayjs(data[0].paymentDetails?.creationDate);

                    // If we don't have payment details in Adyen and creation date is older than a
                    // day, we delete the payment account, because then we are out of sync.
                    if (creationDate.isValid() && dayjs().diff(creationDate, 'hour') > 24) {
                        deleteAccount();
                    } else {
                        redirectPage();
                    }
                } else {
                    dispatch({ type: SET_RECURRING_PAYMENT_DETAILS, payload: data });
                    setLoading(false);
                }
            })
            .catch(error => {
                const edenredRefreshFailed = error?.data?.includes('/connect/token');
                const edenredSessionExpired = error?.data?.includes('Edenred session expired');

                if (edenredRefreshFailed || edenredSessionExpired) {
                    redirectPage();
                } else {
                    dispatch({ type: SET_ERROR, payload: true });
                }
            });
    }, [migrationState, accessToken, merchant, redirectPage, deleteAccount]);

    // /redirect is the only public path. Otherwise show the loader while the
    // cutover probe is in flight or redirecting, and during the normal startup load.
    if (pathname !== '/redirect' && (migrationState !== 'stay' || isLoading)) {
        return <CenteredLoader />;
    }

    return children;
}

StartupLoader.propTypes = {
    children: PropTypes.node.isRequired,
};

export default StartupLoader;
